import type { QueryClient } from "@tanstack/react-query";
import {
  aggregateMemberAttendanceStats,
  fetchAttendanceLogsForStudents,
  type MemberAttendanceAggregate,
} from "@/lib/services/attendanceService";
import { QueryKeys } from "@/lib/types";
import {
  MEMBER_ATTENDANCE_LOOKBACK_DAYS,
  memberAttendanceDateRange,
} from "@/lib/utils/attendance/memberAttendanceDashboard";

function normalizeStudentIds(studentIds: number[]): number[] {
  return [...new Set(studentIds.filter((id) => Number.isFinite(id)))].sort(
    (a, b) => a - b,
  );
}

export async function prefetchMemberAttendanceStats(
  studentIds: number[],
  queryClient: QueryClient,
  accessToken: string,
) {
  const ids = normalizeStudentIds(studentIds);
  if (ids.length === 0) return queryClient;

  const sortedKey = ids.join(",");

  await queryClient.prefetchQuery({
    queryKey: [
      QueryKeys.Attendance,
      "member-dashboard",
      sortedKey,
      MEMBER_ATTENDANCE_LOOKBACK_DAYS,
    ] as const,
    queryFn: async (): Promise<MemberAttendanceAggregate> => {
      const { fromDate, toDate } = memberAttendanceDateRange();
      const logs = await fetchAttendanceLogsForStudents(
        accessToken,
        ids,
        fromDate,
        toDate,
      );
      return aggregateMemberAttendanceStats(logs);
    },
    staleTime: 60_000,
  });

  return queryClient;
}
